import { PlaceholderOption } from "@/components/rich-editor/rich-editor.types.ts";
import { VariableInsertMenu } from "@/components/variable-insert-menu/VariableInsertMenu.tsx";
import { Box, Textarea } from "@skailer-com/uikit";
import { ComponentPropsWithoutRef, forwardRef, useRef } from "react";
import { twMerge } from "tailwind-merge";

interface TextareaWithVariableInsertProps extends Omit<
  ComponentPropsWithoutRef<typeof Textarea>,
  "rightIcon"
> {
  variableOptions: PlaceholderOption[];
  disableSpacing?: boolean;
}

export const TextareaWithVariableInsert = forwardRef<
  HTMLTextAreaElement,
  TextareaWithVariableInsertProps
>(function TextareaWithVariableInsert(
  { className, onChange, onInput, onScroll, variableOptions, disableSpacing, ...textareaProps },
  forwardedRef
) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const setTextareaRef = (node: HTMLTextAreaElement | null) => {
    textareaRef.current = node;

    if (typeof forwardedRef === "function") {
      forwardedRef(node);
      return;
    }

    if (forwardedRef) {
      forwardedRef.current = node;
    }
  };

  const insertVariable = (variable: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const currentValue = textarea.value ?? "";
    const selectionStart = textarea.selectionStart ?? currentValue.length;
    const selectionEnd = textarea.selectionEnd ?? currentValue.length;

    const beforeSelection = currentValue.slice(0, selectionStart);
    const spacer =
      !disableSpacing && beforeSelection.length > 0 && !/\s$/.test(beforeSelection) ? " " : "";
    const insertedText = disableSpacing ? variable : `${spacer}${variable} `;

    textarea.setRangeText(insertedText, selectionStart, selectionEnd, "end");
    textarea.dispatchEvent(new Event("input", { bubbles: true }));

    requestAnimationFrame(() => {
      textarea.focus();
    });
  };

  return (
    <Box className={twMerge("relative w-full", className)}>
      <Textarea
        {...textareaProps}
        ref={setTextareaRef}
        autoCorrect={textareaProps.autoCorrect ?? "off"}
        autoCapitalize={textareaProps.autoCapitalize ?? "off"}
        spellCheck={textareaProps.spellCheck ?? false}
        onInput={onInput}
        onChange={onChange}
        onScroll={onScroll}
        className="[&_textarea]:pr-40"
      />
      <Box className="absolute top-8 right-0 -translate-y-1/2 z-10">
        <VariableInsertMenu options={variableOptions} onInsert={insertVariable} />
      </Box>
    </Box>
  );
});
